// Shared buffer of recent stroke curves (boat accel + erg force).
// Inputs (window events):
//   'crewz-accel-raw'   {t, a, rate?, source: 'phone'|'case'|'demo', demo?}  raw forward accel
//   'crewz-force-curve' {t, points, demo?}                                  one PM5 drive
// Output: 'crewz-accel-catch' {t, strokeMs, rateSpm, expectedMs, missed, source, demo}
// Stroke = { t0, dur (ms, null while live), pts: [{dt, a}] }.
import { createCatchDetector } from './catchDetector';
import { driveEndIndex, trimDrive } from './driveWindow';

const CASE_PRIORITY_MS = 2000;
const state = {
  boat: [],
  live: null,
  erg: [],
  expectedMs: 2000,
  yRange: { boat: null, erg: null },
  extremes: { boat: [], erg: [] },
};
const listeners = new Set();
const detector = createCatchDetector();
let demoOnly = false;
let demoTimer = null;
let lastCaseT = 0;
let activeSource = null;

const notify = () => listeners.forEach((fn) => fn());

function recordRange(kind, pts) {
  if (!pts.length) return;
  let lo = Infinity; let hi = -Infinity;
  pts.forEach((p) => { if (p.a < lo) lo = p.a; if (p.a > hi) hi = p.a; });
  const ex = [...state.extremes[kind], [lo, hi]].slice(-10);
  state.extremes[kind] = ex;
  const min = Math.min(0, ...ex.map((e) => e[0]));
  const max = Math.max(0, ...ex.map((e) => e[1]));
  const pad = (max - min) * 0.1 || 0.5;
  state.yRange[kind] = [min < 0 ? min - pad : 0, max + pad];
}

function onRawSample(d) {
  const t = d.t || Date.now();
  if (d.source === 'case') lastCaseT = t;
  else if (d.source === 'phone' && t - lastCaseT < CASE_PRIORITY_MS) return;
  if (activeSource !== d.source) { activeSource = d.source; detector.reset(); state.live = null; }

  const { a, catchEvt, expectedMs } = detector.push(t, d.a, d.rate || 0);
  state.expectedMs = expectedMs;
  if (state.live) {
    state.live.pts.push({ dt: t - state.live.t0, a, t });
    if (state.live.end == null) {
      const i = driveEndIndex(state.live.pts);
      if (i >= 0) state.live.end = state.live.pts[i].dt;
    }
  }

  if (catchEvt) {
    const ct = catchEvt.t;
    const prev = state.live;
    const carry = prev ? prev.pts.filter((p) => p.t >= ct) : [];
    if (prev) {
      const done = prev.pts.filter((p) => p.t < ct);
      const drive = trimDrive(done, true);
      if (drive.length > 1) {
        const stroke = { t0: prev.t0, dur: drive[drive.length - 1].dt, pts: drive };
        state.boat = [...state.boat, stroke].slice(-3);
        recordRange('boat', drive);
      }
    }
    state.live = { t0: ct, dur: null, end: null, pts: carry.map((p) => ({ dt: p.t - ct, a: p.a, t: p.t })) };
    if (catchEvt.missed) {
      // eslint-disable-next-line no-console
      console.debug('[catch] missed catch — used window minimum', { t: ct, a: catchEvt.a, missed: detector.missedCount });
      window.dispatchEvent(new CustomEvent('crewz-missed-catch', { detail: { t: ct, count: detector.missedCount } }));
    }
    window.dispatchEvent(new CustomEvent('crewz-accel-catch', {
      detail: {
        t: ct, strokeMs: catchEvt.strokeMs, rateSpm: catchEvt.rateSpm, expectedMs,
        missed: catchEvt.missed, source: d.source, demo: !!d.demo,
      },
    }));
  }
  notify();
}

function pushErgCurve(points) {
  const pts = points.map((v, i) => ({ dt: i, a: v }));
  state.erg = [...state.erg, { t0: Date.now(), dur: Math.max(1, pts.length - 1), pts }].slice(-3);
  recordRange('erg', pts);
  notify();
}

if (typeof window !== 'undefined') {
  window.addEventListener('crewz-accel-raw', (e) => {
    const d = e.detail || {};
    if (!Number.isFinite(d.a) || (demoOnly && !d.demo)) return;
    onRawSample(d);
  });
  window.addEventListener('crewz-force-curve', (e) => {
    const d = e.detail || {};
    if (!Array.isArray(d.points) || d.points.length < 3 || (demoOnly && !d.demo)) return;
    pushErgCurve(d.points);
  });
}

export const subscribe = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
// Strokes to draw, oldest → newest (boat: 2 previous + live, drive half only).
export const getStrokes = (kind) => {
  if (kind === 'erg') return state.erg;
  if (!state.live) return state.boat.slice(-3);
  const { t0, end, pts } = state.live;
  const live = end == null ? { t0, dur: null, pts } : { t0, dur: end, pts: pts.filter((p) => p.dt <= end) };
  return [...state.boat.slice(-2), live];
};
// Expected drive duration (x-scale for the live drive before its finish is found).
export const getExpectedDriveMs = () => {
  const d = state.boat.slice(-3).map((s) => s.dur).filter(Boolean);
  return d.length ? d.reduce((s, v) => s + v, 0) / d.length : 0.45 * state.expectedMs;
};
export const getCompletedStrokes = (kind) => (kind === 'erg' ? state.erg : state.boat).slice(-3);
export const lastFullStroke = (kind) => getCompletedStrokes(kind).slice(-1)[0] || null;
export const getExpectedMs = () => state.expectedMs;
export const getYRange = (kind) => state.yRange[kind];

export function resetCurves() {
  state.boat = [];
  state.live = null;
  state.erg = [];
  state.yRange = { boat: null, erg: null };
  state.extremes = { boat: [], erg: [] };
  detector.reset();
  activeSource = null;
  notify();
}

// Compact [[dt, value], ...] with ≤ maxPts points; last point dt = stroke duration.
export function compactStroke(stroke, maxPts = 60) {
  const pts = stroke.pts || [];
  if (!pts.length) return [];
  const step = Math.max(1, Math.ceil(pts.length / maxPts));
  const r = (v) => Math.round(v * 100) / 100;
  const out = [];
  for (let i = 0; i < pts.length; i += step) out.push([Math.round(pts[i].dt), r(pts[i].a)]);
  const endDt = Math.round(stroke.dur ?? pts[pts.length - 1].dt);
  if (out[out.length - 1][0] !== endDt) out.push([endDt, r(pts[pts.length - 1].a)]);
  return out;
}

const gauss = (p, mu, s) => Math.exp(-((p - mu) ** 2) / (2 * s * s));
function demoAccel(p, amp) {
  let a = -3.6 * gauss(p, 0.03, 0.022) + 5.4 * amp * gauss(p, 0.15, 0.055)
    + 1.4 * gauss(p, 0.27, 0.035) - 1.3 * gauss(p, 0.38, 0.03);
  if (p > 0.4) a += -0.45 + 0.4 * Math.sin(2 * Math.PI * (p - 0.4) * 5) * Math.exp(-(p - 0.4) * 3);
  return a + (Math.random() - 0.5) * 0.18;
}
function demoForce(amp) {
  const n = 32;
  return Array.from({ length: n }, (_, i) => {
    const x = i / (n - 1);
    const f = 820 * amp * Math.sin(Math.PI * x) ** 1.4 * (1 + 0.22 * Math.sin(Math.PI * x * 0.9 + 0.3) - 0.12 * x);
    return Math.max(0, f + (Math.random() - 0.5) * 18);
  });
}

// Demo Mode: boat raw accel at 50 Hz goes through the real catch detector;
// erg emits one force curve per stroke.
export function startDemo(kind, getRate) {
  stopDemo();
  demoOnly = true;
  resetCurves();
  let strokeStart = Date.now();
  let period = 60000 / (getRate() || 24);
  let amp = 1;
  demoTimer = setInterval(() => {
    const now = Date.now();
    if (now - strokeStart >= period) {
      strokeStart = now - strokeStart >= 2 * period ? now : strokeStart + period;
      period = 60000 / (getRate() || 24);
      amp = 0.88 + Math.random() * 0.24;
      if (kind === 'erg') {
        window.dispatchEvent(new CustomEvent('crewz-force-curve', { detail: { t: now, points: demoForce(amp), demo: true } }));
      }
    }
    if (kind === 'boat') {
      const a = demoAccel((now - strokeStart) / period, amp) + 0.3;
      window.dispatchEvent(new CustomEvent('crewz-accel-raw', { detail: { t: now, a, rate: getRate(), source: 'demo', demo: true } }));
    }
  }, 20);
}

export function stopDemo() {
  if (demoTimer) clearInterval(demoTimer);
  demoTimer = null;
  demoOnly = false;
}
